import React, { useContext, useMemo } from 'react';
import { MDBRow, MDBCol, MDBInput, MDBSelect, MDBBtn, MDBSpinner, MDBFile, MDBInputGroup, MDBSwitch, MDBIcon, MDBTimepicker } from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from "react-country-flag";
import { getLocalizedText } from "../../../utils/localizedText";
import ReactPDF from "@react-pdf/renderer";
import getEventDays from "../../../utils/getEventDays";
import { UserContext } from "../../../App";
import SlugField from "../../../components/toolbox/SlugField";
import { isAdminOrModerator } from "../../../utils/roles";
import { pickSourceText } from "../../../utils/slugify";

const GeneralTab = ({
  event,
  industries,
  selectedIndustryIds,
  setSelectedIndustryIds,
  onInputChange,
  onImageUpload,
  onBadgePreviewUpload,
  onFileUpload,
  onRemoveFile,
  onDownloadFile,
  onProgramUpload,
  onProgramRemove,
  onProgramDownload,
  isLoading
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useContext(UserContext);
  // Public URL slug: Admin/Moderator only (useEventData strips it from the input otherwise)
  const canEditSlug = isAdminOrModerator(user);

  return (
    <>
      <MDBRow>
        <MDBCol size="12" xl="3">
          {industries?.length > 0 && (
            <MDBSelect
              label={t("Select Industries")}
              multiple
              data={industries?.map(industry => ({
                text: getLocalizedText(industry.name),
                value: industry.id
              }))}
              value={selectedIndustryIds}
              onChange={(entries) => setSelectedIndustryIds(entries.map(entry => entry.value))}
            />
          )}
        </MDBCol>
        <MDBCol size="12" md="6" xl="3">
          <MDBSwitch
            label={event?.status === 'active' ? t('Active') : t('Disabled')}
            checked={event?.status === 'active'}
            onChange={(e) => onInputChange('status', e.target.checked ? 'active' : 'disabled')}

          />
        </MDBCol>
      </MDBRow>


      {/* Name Section */}
      <div className="my-2">
        <h5>{t("Name")}</h5>
        <MDBRow>
          <MDBCol size="12" xl="6">
            <MDBInputGroup className="mb-1">
              <div className="input-group-text">
                <ReactCountryFlag countryCode="de" svg style={{ width: "20px", height: "20px" }} />
              </div>
              <MDBInput
                label={t("German")}
                type="text"
                value={event?.name?.find((t) => t.language === "de")?.text || ""}
                onChange={(e) => onInputChange('name', e.target.value, 'de')}
              />
            </MDBInputGroup>
          </MDBCol>
          <MDBCol size="12" xl="6">
            <MDBInputGroup>
              <div className="input-group-text">
                <ReactCountryFlag countryCode="gb" svg style={{ width: "20px", height: "20px" }} />
              </div>
              <MDBInput
                label={t("English")}
                type="text"
                value={event?.name?.find((t) => t.language === "en")?.text || ""}
                onChange={(e) => onInputChange('name', e.target.value, 'en')}
              />
            </MDBInputGroup>
          </MDBCol>
        </MDBRow>
        {/* Public URL slug (Admin/Moderator only) */}
        {canEditSlug && (
          <MDBRow className="mt-3">
            <MDBCol size="12" xl="6">
              <SlugField
                entity="event"
                entityId={event?.id}
                value={event?.slug}
                history={event?.slugHistory}
                sourceText={pickSourceText(event?.name)}
                disabled={isLoading}
                onChange={(slug) => onInputChange('slug', slug)}
              />
            </MDBCol>
          </MDBRow>
        )}
      </div>

      {/* Type and Industry Section */}
      <h5 className="mt-4">{t("Settings")}</h5>
      <MDBRow>
        <MDBCol md="6" xl="3">
          <MDBSelect
            label={t("Select Event Type")}
            data={[
              { text: t("Select Event Type"), value: "" },
              { text: t("Fair"), value: "fair" },
              { text: t("Seminar"), value: "seminar" }
            ]}
            value={event.type}
            onChange={(value) => onInputChange('type', value.value)}
          />
        </MDBCol>

      </MDBRow>
      <MDBRow className="mt-3">
        <MDBCol md="6" xl="3">
          <MDBInput
            label={t('Landing Page URL')}
            type="url"
            value={event?.landingPageUrl || ''}
            onChange={(e) => onInputChange('landingPageUrl', e.target.value)}
          />
        </MDBCol>
      </MDBRow>
      <MDBRow className="mt-3">
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Start Date')}
            value={event?.startDate || ''}
            onChange={(e) => onInputChange('startDate', e.target.value)}
          />
        </MDBCol>
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('End Date')}
            value={event?.endDate || ''}
            onChange={(e) => onInputChange('endDate', e.target.value)}
          />
        </MDBCol>
      </MDBRow>
      <DaySchedulesSection
        event={event}
        onInputChange={onInputChange}
      />
      <h6 className="mt-3">{t("Lectures and Actions")}</h6>
      <MDBRow className="mt-3">
        <MDBCol md="6" xl="3">
          <MDBSwitch
            label={t('Exhibitor lectures')}
            checked={event?.exhibitorLectures}
            onChange={(e) => onInputChange('exhibitorLectures', e.target.checked)}
          />
        </MDBCol>
      </MDBRow>
      <MDBRow className="mt-3">
        <MDBCol md="6" xl="1">
          <MDBInput
            type="number"
            min="0"
            step="1"
            label={t('Lecture rooms')}
            value={event?.lectureRooms || ''}
            onChange={(e) => onInputChange('lectureRooms', e.target.value)}
          />
        </MDBCol>
      </MDBRow>
      <MDBRow className="mt-3">
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Lecture registration start date')}
            value={event?.lectureRegistrationStartDate || ''}
            onChange={(e) => onInputChange('lectureRegistrationStartDate', e.target.value)}
          />
        </MDBCol>
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Lecture registration end date')}
            value={event?.lectureRegistrationEndDate || ''}
            onChange={(e) => onInputChange('lectureRegistrationEndDate', e.target.value)}
          />
        </MDBCol>
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Lecture edit end date')}
            value={event?.lectureEditEndDate || ''}
            onChange={(e) => onInputChange('lectureEditEndDate', e.target.value)}
          />
        </MDBCol>
      </MDBRow>
      <MDBRow className="mt-3">
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Lecture document upload end date')}
            value={event?.lectureDocumentsEndDate || ''}
            onChange={(e) => onInputChange('lectureDocumentsEndDate', e.target.value)}
          />
        </MDBCol>
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Lecture presentation end date')}
            value={event?.lecturePresentationEndDate || ''}
            onChange={(e) => onInputChange('lecturePresentationEndDate', e.target.value)}
          />
        </MDBCol>
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Lecture recording correction end date')}
            value={event?.lectureRecordingCorrectionEndDate || ''}
            onChange={(e) => onInputChange('lectureRecordingCorrectionEndDate', e.target.value)}
          />
        </MDBCol>
      </MDBRow>
      <MDBRow className="mt-3">
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Action registration start date')}
            value={event?.actionRegistrationStartDate || ''}
            onChange={(e) => onInputChange('actionRegistrationStartDate', e.target.value)}
          />
        </MDBCol>
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Action registration end date')}
            value={event?.actionRegistrationEndDate || ''}
            onChange={(e) => onInputChange('actionRegistrationEndDate', e.target.value)}
          />
        </MDBCol>
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Action edit end date')}
            value={event?.actionEditEndDate || ''}
            onChange={(e) => onInputChange('actionEditEndDate', e.target.value)}
          />
        </MDBCol>
      </MDBRow>
      <h6 className="mt-3">{t("Booths")}</h6>
      <MDBRow className="mt-3">
        <MDBCol md="6" xl="3">
          <MDBSwitch
            label={t('Booth booking')}
            checked={event?.boothBooking}
            onChange={(e) => onInputChange('boothBooking', e.target.checked)}
          />
        </MDBCol>
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Booth booking start date')}
            value={event?.boothBookingStart || ''}
            max={event?.boothBookingEnd || undefined}
            onChange={(e) => onInputChange('boothBookingStart', e.target.value)}
          />
        </MDBCol>
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Booth booking end date')}
            value={event?.boothBookingEnd || ''}
            min={event?.boothBookingStart || undefined}
            onChange={(e) => onInputChange('boothBookingEnd', e.target.value)}
          />
        </MDBCol>
        <MDBCol size="12" xl="3" className="d-flex align-items-center">
          <small className="text-muted">
            {t('Hall plan booking is only possible from the start date through the end date (both optional) and only while booth booking is on.')}
          </small>
        </MDBCol>
      </MDBRow>
      <MDBRow className="mt-3">

        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Equipment booking start date')}
            value={event?.startBoothEquipmentBooking || ''}
            onChange={(e) => onInputChange('startBoothEquipmentBooking', e.target.value)}
          />
        </MDBCol>
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Equipment booking end date')}
            value={event?.deadlineBoothEquipmentBooking || ''}
            onChange={(e) => onInputChange('deadlineBoothEquipmentBooking', e.target.value)}
          />
        </MDBCol>
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Marketing booking start date')}
            value={event?.startBoothMarketingBooking || ''}
            onChange={(e) => onInputChange('startBoothMarketingBooking', e.target.value)}
          />
        </MDBCol>
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Marketing booking end date')}
            value={event?.deadlineBoothMarketingBooking || ''}
            onChange={(e) => onInputChange('deadlineBoothMarketingBooking', e.target.value)}
          />
        </MDBCol>
      </MDBRow>
      <MDBRow className="mt-3">
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Upload print files end date')}
            value={event?.deadlineBoothEquipmentPrint || ''}
            onChange={(e) => onInputChange('deadlineBoothEquipmentPrint', e.target.value)}
          />
        </MDBCol>
        <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Carpet color selection end date')}
            value={event?.carpetColorSelectionEndDate || ''}
            onChange={(e) => onInputChange('carpetColorSelectionEndDate', e.target.value)}
          />
        </MDBCol>
      </MDBRow>
      <h6 className="mt-3">{t("Ticket registration")}</h6>
      <MDBRow className="mt-3">
        <MDBCol md="6" xl="3">
          <MDBSwitch
            label={t('Enable ticket booking')}
            checked={event?.enableTicketBooking}
            onChange={(e) => onInputChange('enableTicketBooking', e.target.checked)}
          />
        </MDBCol>
        {event?.enableTicketBooking && <MDBCol md="6" xl="3">
          <MDBInput
            type="date"
            label={t('Ticket self cancelation deadline')}
            value={event?.ticketCancellationDeadline || ''}
            onChange={(e) => onInputChange('ticketCancellationDeadline', e.target.value)}
          />
        </MDBCol>}
      </MDBRow>
      {event?.enableTicketBooking &&
        <MDBRow className="mt-3">
          <MDBCol md="6" xl="3">
            <MDBInput
              label={t('Suggested Invitation Code')}
              value={event?.suggestedInvitationCode || ''}
              onChange={(e) => onInputChange('suggestedInvitationCode', e.target.value)}
            />
          </MDBCol>
        </MDBRow>
      }
      <MDBRow className="mt-3">
        <MDBCol md="6" xl="3">
          <MDBSwitch
            label={t('Invitation codes')}
            checked={event?.hasInvitationCodes}
            onChange={(e) => onInputChange('hasInvitationCodes', e.target.checked)}
          />
        </MDBCol>
        {event?.hasInvitationCodes && (
          <MDBCol md="6" xl="3">
            <MDBSwitch
              label={t('Invitation code required')}
              checked={event?.invitationCodeRequired}
              onChange={(e) => onInputChange('invitationCodeRequired', e.target.checked)}
            />
          </MDBCol>
        )}
      </MDBRow>
      <MDBRow className="mt-3">
        <MDBCol md="6" xl="3">
          <MDBSwitch
            label={t('Selectable participant days')}
            checked={event?.bookingSelectDays}
            onChange={(e) => onInputChange('bookingSelectDays', e.target.checked)}
          />
        </MDBCol>
        <MDBCol md="6" xl="3">
          <MDBSwitch
            label={t('Ticket price per day')}
            checked={event?.ticketPricePerDay}
            onChange={(e) => onInputChange('ticketPricePerDay', e.target.checked)}
          />
        </MDBCol>
        <MDBCol md="12" xl="6">
          {(() => {
            // Derive the active flow from paymentFlow (new field) with fallback to legacy
            // booleans so existing events render correctly until next save.
            const activeFlow = event?.paymentFlow
              || (event?.instantPaymentOnly ? 'instant_stripe'
                  : event?.autoGenerateInvoice ? 'instant_stripe'
                  : 'manual_invoice');
            // Selecting a flow writes paymentFlow AND syncs the legacy booleans so any
            // frontend code that still reads them stays consistent.
            const setFlow = (flow) => {
              onInputChange('paymentFlow', flow);
              onInputChange('instantPaymentOnly', flow === 'instant_stripe');
              onInputChange('autoGenerateInvoice', flow === 'instant_stripe');
            };
            const options = [
              {
                value: 'instant_stripe',
                label: t('Stripe / instant payment'),
                hint: t('Customer pays online via Stripe at checkout. Invoice generated automatically after payment.')
              },
              {
                value: 'manual_invoice',
                label: t('Manual invoice'),
                hint: t('Backoffice issues the invoice manually. Customer pays by bank transfer. No online payment.')
              },
              {
                value: 'review_then_pay',
                label: t('Review then online payment'),
                hint: t('Customer orders, backoffice reviews and approves. After approval the customer receives an email and can pay online via Stripe.')
              }
            ];
            return (
              <div>
                <label className="form-label fw-bold mb-2">{t('Payment flow')}</label>
                <div className="d-flex flex-column gap-2">
                  {options.map(opt => (
                    <div key={opt.value} className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="paymentFlow"
                        id={`paymentFlow-${opt.value}`}
                        value={opt.value}
                        checked={activeFlow === opt.value}
                        onChange={() => setFlow(opt.value)}
                      />
                      <label className="form-check-label" htmlFor={`paymentFlow-${opt.value}`}>
                        <span className="fw-semibold">{opt.label}</span>
                        <small className="text-muted d-block">{opt.hint}</small>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </MDBCol>
      </MDBRow>
      {event?.enableTicketBooking &&
        <>
          <MDBRow className="mt-3">
            <MDBCol md="6" xl="3">
              <MDBSwitch
                label={t('Staff ticket booking for users')}
                checked={event?.enableStaffBooking}
                onChange={(e) => onInputChange('enableStaffBooking', e.target.checked)}
              />
            </MDBCol>
          </MDBRow>
          <MDBRow className="mt-3">
            <MDBCol md="6" xl="3">
              <MDBSwitch
                label={t('Allow companies to book tickets for employees')}
                checked={event?.allowCompanyToBookTickets}
                onChange={(e) => onInputChange('allowCompanyToBookTickets', e.target.checked)}
              />
            </MDBCol>
          </MDBRow>
        </>
      }
      {/* Image Upload Section */}
      <h5 className="mt-4">{t("Image")}</h5>
      <MDBRow>
        <MDBCol md="6">
          <div className="text-center mb-4">
            <img
              src={event?.image?.fileName ?
                "https://iec-cp-public.s3.eu-central-1.amazonaws.com/EventImages/" + event.image.fileName
                : "https://placehold.co/600x400"}
              className="img-thumbnail"
              style={{ height: "200px", width: "400px", objectFit: "cover" }}
              alt={t("Event image")}
            />
            <MDBFile
              id='eventImage'
              onChange={e => onImageUpload(e.target.files[0])}
              className="mt-2"
            />
          </div>
        </MDBCol>
      </MDBRow>


      {/* Floor Plan Section */}
      <h5>{t('Floor Plan')}</h5>
      <MDBRow>
        <MDBCol md="6">
          {/* <ReactPDF
          file={event.floorPlan.s3Path}
        /> */}
          {event?.floorPlan?.fileName && (
            <div className="text-center mb-2">
              <MDBBtn
                color="link"
                onClick={() => onDownloadFile(event.floorPlan.s3Path)}
                size="sm"
              >
                {event.floorPlan.fileName}
              </MDBBtn>
              <br />
              <MDBBtn
                color="link"
                className="text-danger"
                size="sm"
                onClick={() => onRemoveFile(event.floorPlan.s3Path)}
              >
                {t("Remove")}
              </MDBBtn>
            </div>
          )}
          <MDBFile
            id='floorPlan'
            onChange={e => onFileUpload(e.target.files[0])}
            className="mb-4"
          />
        </MDBCol>
      </MDBRow>

      {/* Program Section */}
      <h5>{t('Program')}</h5>
      <MDBRow>
        <MDBCol md="6">
          {event?.program?.fileName && (
            <div className="text-center mb-2">
              <MDBBtn
                color="link"
                onClick={() => onProgramDownload(event.program.s3Path)}
                size="sm"
              >
                {event.program.fileName}
              </MDBBtn>
              <br />
              <MDBBtn
                color="link"
                className="text-danger"
                size="sm"
                onClick={() => onProgramRemove(event.program.s3Path)}
              >
                {t("Remove")}
              </MDBBtn>
            </div>
          )}
          <MDBFile
            id='program'
            onChange={e => onProgramUpload(e.target.files[0])}
            className="mb-4"
          />
        </MDBCol>
      </MDBRow>

      {/* Badge Preview Section */}
      <h5 className="mt-4">{t("Badge Preview")}</h5>
      <MDBRow>
        <MDBCol md="6">
          <div className="text-center mb-4">
            <img
              src={event?.badgePreview?.fileName ?
                `https://iec-cp-public.s3.eu-central-1.amazonaws.com/EventImages/${event.badgePreview.fileName}`
                : "https://placehold.co/500x600"}
              className="img-thumbnail"
              style={{ height: "400px", width: "327px", objectFit: "contain" }}
              alt={t("Badge preview")}
            />
            <MDBFile
              id='badgePreview'
              onChange={e => onBadgePreviewUpload(e.target.files[0])}
              className="mt-2"
            />
          </div>
        </MDBCol>
      </MDBRow>
    </>
  );
};

const DaySchedulesSection = ({ event, onInputChange }) => {
  const { t } = useTranslation();

  const eventDays = useMemo(() => {
    if (!event?.startDate || !event?.endDate) return [];
    try {
      return getEventDays({ startDate: event.startDate, endDate: event.endDate });
    } catch {
      return [];
    }
  }, [event?.startDate, event?.endDate]);

  const schedulesByDate = useMemo(() => {
    const map = new Map();
    (event?.daySchedules || []).forEach(s => {
      if (s?.date) map.set(s.date, s);
    });
    return map;
  }, [event?.daySchedules]);

  const writeSchedule = (date, patch) => {
    const existing = schedulesByDate.get(date) || { date };
    const merged = { ...existing, ...patch };
    const next = eventDays.map((d, idx) => {
      if (d === date) return { ...merged, day: idx + 1, date: d };
      const s = schedulesByDate.get(d);
      return s ? { ...s, day: idx + 1, date: d } : null;
    }).filter(Boolean);
    onInputChange('daySchedules', next);
  };

  const generateFromRange = () => {
    if (eventDays.length === 0) return;
    const next = eventDays.map((d, idx) => {
      const existing = schedulesByDate.get(d);
      return existing
        ? { ...existing, day: idx + 1, date: d }
        : {
            day: idx + 1,
            date: d,
            exhibitorStart: event?.startTime || '',
            exhibitorEnd: event?.endTime || '',
            visitorStart: event?.startTime || '',
            visitorEnd: event?.endTime || '',
          };
    });
    onInputChange('daySchedules', next);
  };

  const applyFromFirstDay = (audience) => {
    if (eventDays.length === 0) return;
    const first = schedulesByDate.get(eventDays[0]);
    if (!first) return;
    const next = eventDays.map((d, idx) => {
      const existing = schedulesByDate.get(d) || { date: d };
      const patch = audience === 'visitor'
        ? { visitorStart: first.visitorStart, visitorEnd: first.visitorEnd }
        : { exhibitorStart: first.exhibitorStart, exhibitorEnd: first.exhibitorEnd };
      return { ...existing, ...patch, day: idx + 1, date: d };
    });
    onInputChange('daySchedules', next);
  };

  if (eventDays.length === 0) {
    return (
      <div className="mt-4">
        <h6>{t('Opening Hours per Day')}</h6>
        <p className="text-muted small mb-0">
          {t('Set start and end date first, then opening hours can be entered per day.')}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="mb-0">{t('Opening Hours per Day')}</h6>
        <div>
          <MDBBtn
            size="sm"
            color="secondary"
            outline
            className="me-2"
            onClick={generateFromRange}
            type="button"
          >
            <MDBIcon fas icon="sync-alt" className="me-1" />
            {t('Generate days from date range')}
          </MDBBtn>
          <MDBBtn
            size="sm"
            color="secondary"
            outline
            className="me-2"
            onClick={() => applyFromFirstDay('exhibitor')}
            type="button"
            disabled={!schedulesByDate.has(eventDays[0])}
          >
            {t('Apply exhibitor hours from day 1 to all')}
          </MDBBtn>
          <MDBBtn
            size="sm"
            color="secondary"
            outline
            onClick={() => applyFromFirstDay('visitor')}
            type="button"
            disabled={!schedulesByDate.has(eventDays[0])}
          >
            {t('Apply visitor hours from day 1 to all')}
          </MDBBtn>
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th style={{ width: '60px' }}>{t('Day')}</th>
              <th style={{ width: '140px' }}>{t('Date')}</th>
              <th>{t('Exhibitor start')}</th>
              <th>{t('Exhibitor end')}</th>
              <th>{t('Visitor start')}</th>
              <th>{t('Visitor end')}</th>
            </tr>
          </thead>
          <tbody>
            {eventDays.map((date, idx) => {
              const s = schedulesByDate.get(date) || {};
              return (
                <tr key={date}>
                  <td>{idx + 1}</td>
                  <td>{date}</td>
                  <td>
                    <MDBTimepicker
                      format="24h"
                      value={s.exhibitorStart || ''}
                      onChange={(value) => writeSchedule(date, { exhibitorStart: value })}
                    />
                  </td>
                  <td>
                    <MDBTimepicker
                      format="24h"
                      value={s.exhibitorEnd || ''}
                      onChange={(value) => writeSchedule(date, { exhibitorEnd: value })}
                    />
                  </td>
                  <td>
                    <MDBTimepicker
                      format="24h"
                      value={s.visitorStart || ''}
                      onChange={(value) => writeSchedule(date, { visitorStart: value })}
                    />
                  </td>
                  <td>
                    <MDBTimepicker
                      format="24h"
                      value={s.visitorEnd || ''}
                      onChange={(value) => writeSchedule(date, { visitorEnd: value })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GeneralTab;